import { useCookie, useRuntimeConfig } from '#imports'
import { computed, reactive, ref, watch } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import {
  DIDOMI_COOKIE_NAME,
  VENDORS_ID,
  VENDORS_SDK_ID,
} from '../didomi/config'

import { useDisableTrackers } from './useDisableTrackers'

export function useDataConsent() {
  const { market } = useMarketplace()
  const {
    tracking: { countriesWithNoCookieOptIn, didomi },
  } = useRuntimeConfig().public
  const cookie = useCookie<string | null>(DIDOMI_COOKIE_NAME)
  const logger = useLogger()
  const trackersDisabled = useDisableTrackers()

  const noticeIdForCountry = didomi.noticeId[market.countryCode]

  const isDidomiEnabled =
    !!noticeIdForCountry &&
    noticeIdForCountry !== 'unknown' &&
    !trackersDisabled

  const vendorAcceptanceStates = reactive<
    Record<keyof typeof VENDORS_ID, boolean>
  >({
    amplitude: false,
    braze: false,
    gtm: false,
    appsFlyer: false,
    youtube: false,
  })

  const isDidomiReady = ref(false)

  let hasLoggedMissingVendors = false

  // Determine if modal should be displayed based on the country and the existence of the cookie consent cookie
  const shouldDisplayModal = (hasConsentCookie: boolean) => {
    return (
      !countriesWithNoCookieOptIn.includes(market.countryCode) &&
      !hasConsentCookie
    )
  }

  const isCookiesModalDisplayed = ref(shouldDisplayModal(!!cookie.value))

  watch(
    cookie,
    () => {
      isCookiesModalDisplayed.value = shouldDisplayModal(!!cookie.value)
    },
    { immediate: false },
  )

  function showCookiesModal() {
    if (!import.meta.client) return

    window.Didomi?.preferences?.show?.()
  }

  function getUserConsentForVendor(
    vendor: (typeof VENDORS_ID)[keyof typeof VENDORS_ID],
  ): boolean {
    try {
      if (!isDidomiEnabled || !import.meta.client || !window.Didomi)
        return false

      if (
        !window.Didomi.getCurrentUserStatus ||
        typeof window.Didomi.getCurrentUserStatus !== 'function'
      ) {
        return false
      }

      if (
        !window.Didomi.getCurrentUserStatus().vendors ||
        !window.Didomi.getCurrentUserStatus().vendors?.[vendor]
      ) {
        if (!hasLoggedMissingVendors) {
          logger.error(
            `[Didomi] Error getting vendors (triggered by ${vendor})`,
            {
              vendors: window.Didomi.getCurrentUserStatus()?.vendors,
              owners: ['bot-group-onboard-front'],
            },
          )

          hasLoggedMissingVendors = true
        }

        return false
      }

      return window.Didomi.getCurrentUserStatus().vendors[vendor].enabled
    } catch (error) {
      logger.error(`[Didomi] Error getting user consent for vendor ${vendor}`, {
        error: error as Error,
        owners: ['bot-group-onboard-front'],
      })

      return false
    }
  }

  function getUserConsentForAmplitude() {
    return getUserConsentForVendor(VENDORS_ID.amplitude)
  }

  function getUserConsentForBraze() {
    return getUserConsentForVendor(VENDORS_ID.braze)
  }

  function getNoticeVisible() {
    if (!isDidomiEnabled || !import.meta.client || !window.Didomi) return false

    return window.Didomi.notice?.isVisible() ?? false
  }

  function updateVendorAcceptanceStates() {
    if (!isDidomiEnabled || !import.meta.client || !window.Didomi) return

    if (
      typeof window.Didomi.getCurrentUserStatus !== 'function' ||
      !window.Didomi.getCurrentUserStatus().vendors
    ) {
      return
    }

    isDidomiReady.value = true

    for (const [vendorKey, vendorId] of Object.entries(VENDORS_ID)) {
      const vendor = vendorKey as keyof typeof VENDORS_ID
      vendorAcceptanceStates[vendor] = getUserConsentForVendor(vendorId)
    }
  }

  const isAppsFlyerUserConsentGiven = computed(
    () => vendorAcceptanceStates.appsFlyer,
  )

  const isBrazeUserConsentGiven = computed(() => vendorAcceptanceStates.braze)

  const isYoutubeUserConsentGiven = computed(
    () => vendorAcceptanceStates.youtube,
  )

  function acceptYoutubeVendor() {
    if (!import.meta.client || !window.Didomi) return

    // Get the vendor and all its purposes
    const { didomiId, purposeIds } = window.Didomi.getVendorById(
      VENDORS_SDK_ID.youtube,
    )

    const transaction = window.Didomi.openCurrentUserStatusTransaction()

    transaction.enableVendor(didomiId)
    transaction.enablePurposes(purposeIds)
    transaction.commit()
  }

  if (isDidomiEnabled && import.meta.client) {
    window.didomiOnReady = window.didomiOnReady || []
    window.didomiOnReady.push(() => {
      updateVendorAcceptanceStates()

      // consent can be changed by the user at any time during app navigation
      window.Didomi?.on?.('consent.changed', () => {
        updateVendorAcceptanceStates()
      })
    })
  }

  return {
    showCookiesModal,
    getUserConsentForAmplitude,
    getUserConsentForBraze,
    acceptYoutubeVendor,
    isAppsFlyerUserConsentGiven,
    isBrazeUserConsentGiven,
    isYoutubeUserConsentGiven,
    isDidomiEnabled,
    isDidomiReady,
    isCookiesModalDisplayed,
    getNoticeVisible,
  }
}
